import axios from "axios";
import { useContext, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { UserContext } from "../components/UserContext";
import { WorkspaceData } from "../components/Interfaces/Workspace";
import "leaflet/dist/leaflet.css";
import { SlSizeFullscreen } from "react-icons/sl";
import workspaceTypes from "../assets/WorkspaceTypes";
import DOMPurify from "dompurify";
import { FaStar } from "react-icons/fa";
import { ReviewData } from "../components/Interfaces/ReviewData";
import amenities from "../assets/Amenities";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Cookies from "js-cookie";
import { createPortal } from "react-dom";
import { X, CheckCircle } from "lucide-react";
import AuthModal from "../components/AuthModal";
import StripePaymentModal from "../components/StripePaymentModal";
import { toast } from "react-toastify";
import { BookingStatus } from "../constants-generated/well-known-values";
import { BookingType } from "../assets/BookingType";

interface BookingData {
  id: number;
  userId: number;
  workspaceId: number;
  startDate: string;
  endDate: string;
  totalPrice: number;
  status: string;
  bookingType: number;
  message?: string;
  isInstantBookable: boolean;
}

export default function WorkspacePage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState<WorkspaceData | null>(null);
  const [lastConversationId, setLastConversationId] = useState<number | null>(
    null,
  );
  const [isEditable, setIsEditable] = useState(false);
  const [rating, setRating] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const [isInstantBooking, setIsInstantBooking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isTourSubmitting, setIsTourSubmitting] = useState(false);
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [tourBookingSuccess, setTourBookingSuccess] = useState(false);
  const [contactMessageSuccess, setContactMessageSuccess] = useState(false);
  const [showStripeModal, setShowStripeModal] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_userBooking, setUserBooking] = useState<BookingData | null>(null);
  const [workspaceBookings, setWorkspaceBookings] = useState<BookingData[]>([]);

  // Removed unused modal states and form data

  const [activeTab, setActiveTab] = useState<"contact" | "tour" | "reserve">(
    "reserve",
  );
  const [contactFormData, setContactFormData] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    message: "",
  });

  const [moveInDate, setMoveInDate] = useState<Date | null>(new Date());
  const [moveOutDate, setMoveOutDate] = useState<Date | null>(null);
  const [activeInput, setActiveInput] = useState<"moveIn" | "moveOut" | null>(
    null,
  );
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);

  const IconComponent =
    workspaceTypes.find((type) => type.title === workspace?.workspaceType)
      ?.icon || null;
  const { user } = useContext(UserContext);
  const SafeHTML = ({ html }: { html: string }) => (
    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
  );

  // Calculate minimum move-out date based on minimum booking requirements
  const calculateMinimumMoveOutDate = (moveInDate: Date | null): Date => {
    if (!moveInDate || !workspace) {
      return new Date();
    }

    const minDate = new Date(moveInDate);
    const { minimumBookingTime, minimumBookingType } = workspace;

    switch (minimumBookingType) {
      case BookingType.IsHours:
        minDate.setHours(minDate.getHours() + minimumBookingTime);
        break;
      case BookingType.IsDays:
        minDate.setDate(minDate.getDate() + minimumBookingTime);
        break;
      case BookingType.IsWeeks:
        minDate.setDate(minDate.getDate() + minimumBookingTime * 7);
        break;
      case BookingType.IsMonths:
        minDate.setMonth(minDate.getMonth() + minimumBookingTime);
        break;
      case BookingType.IsYears:
        minDate.setFullYear(minDate.getFullYear() + minimumBookingTime);
        break;
      default:
        // Default to next day if no valid booking type
        minDate.setDate(minDate.getDate() + 1);
    }

    return minDate;
  };

  // Form submission handlers
  const handleBookTourSubmit = async () => {
    if (!user) {
      setIsAuthModalOpen(true);
      return;
    }

    if (!moveInDate) {
      toast.warning("Please select a preferred date for the tour");
      return;
    }

    if (!contactFormData.message.trim()) {
      toast.warning("Please enter a message for your tour request");
      return;
    }

    setIsTourSubmitting(true);
    try {
      const token = Cookies.get("authToken");

      const tourRequest = {
        UserId: user.id,
        WorkspaceId: parseInt(workspaceId || "0"),
        PreferredDate: moveInDate.toISOString(),
        Message: contactFormData.message,
      };

      const response = await axios.post("/api/Booking/tour", tourRequest, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200 && response.data.success) {
        setTourBookingSuccess(true);
        if (response.data.conversationId) {
          setLastConversationId(response.data.conversationId);
        }
        setContactFormData({
          name: "",
          phoneNumber: "",
          email: "",
          message: "",
        });
      } else {
        toast.error(response.data.message || "Failed to book tour");
      }
    } catch (error) {
      console.error("Error booking tour:", error);
      toast.error(
        "An error occurred while booking the tour. Please try again.",
      );
    } finally {
      setIsTourSubmitting(false);
    }
  };

  const handleReserveSubmit = async () => {
    if (!user) {
      setIsAuthModalOpen(true);
      return;
    }

    if (!moveInDate || !moveOutDate) {
      toast.warning("Please select move-in and move-out dates");
      return;
    }

    // Check if instant bookable - if so, show Stripe modal
    if (workspace?.isInstantBookable) {
      setShowStripeModal(true);
      return;
    }

    // Regular booking flow (non-instant)
    setIsSubmitting(true);
    try {
      const token = Cookies.get("authToken");
      const pricingInfo = calculatePricing();

      const bookingRequest = {
        UserId: user.id,
        WorkspaceId: parseInt(workspaceId || "0"),
        StartDate: moveInDate.toISOString(),
        EndDate: moveOutDate.toISOString(),
        TotalPrice: pricingInfo.total,
        Status: BookingStatus.Pending,
        Message: `Reservation request from ${moveInDate.toLocaleDateString()} to ${moveOutDate.toLocaleDateString()}`,
      };

      const response = await axios.post("/api/Booking/create", bookingRequest, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200 && response.data.success) {
        setBookingData(response.data.booking);
        setIsInstantBooking(response.data.isInstantBookable === true);
        if (response.data.conversationId) {
          setLastConversationId(response.data.conversationId);
        }
        setShowSuccessModal(true);
      } else {
        toast.error(response.data.message || "Failed to create booking");
      }
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error(
        "An error occurred while creating the booking. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setIsAuthModalOpen(true);
      return;
    }

    if (!contactFormData.message.trim()) {
      toast.warning("Please enter a message");
      return;
    }

    if (!workspace?.userId) {
      toast.error("Unable to send message - workspace owner not found");
      return;
    }

    setIsContactSubmitting(true);
    try {
      const token = Cookies.get("authToken");

      const messageRequest = {
        Content: contactFormData.message,
        SenderId: user.id,
        RecipientId: workspace.userId,
        WorkspaceId: parseInt(workspaceId || "0"),
      };

      const response = await axios.post(
        "/api/Booking/Contact",
        messageRequest,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.status === 200 && response.data.success) {
        setContactMessageSuccess(true);
        if (response.data.conversationId) {
          setLastConversationId(response.data.conversationId);
        }
        setContactFormData({
          name: "",
          phoneNumber: "",
          email: "",
          message: "",
        });
      } else {
        toast.error(response.data.message || "Failed to send message");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(
        "An error occurred while sending the message. Please try again.",
      );
    } finally {
      setIsContactSubmitting(false);
    }
  };

  const handleBookWorkspace = () => {
    // Navigate to edit workspace page or show edit modal
    console.log("Edit workspace clicked");
  };

  const handleContactFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setContactFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Format price with commas
  const formatPrice = (amount: number) => {
    return amount.toLocaleString("en-AU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Calculate pricing based on selected date range
  const calculatePricing = () => {
    if (!moveInDate || !moveOutDate || !workspace) {
      return {
        unit: "days",
        quantity: 0,
        price: 0,
        total: 0,
        description: "Select dates",
      };
    }

    const diffInMs = moveOutDate.getTime() - moveInDate.getTime();
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
    const diffInWeeks = diffInDays / 7;
    const diffInMonths = diffInDays / 30.44; // Average days per month
    const diffInYears = diffInDays / 365.25; // Account for leap years

    // Determine the most appropriate pricing tier
    if (diffInYears >= 1 && workspace.pricePerYear > 0) {
      const years = Math.ceil(diffInYears);
      return {
        unit: "year",
        quantity: years,
        price: workspace.pricePerYear,
        total: years * workspace.pricePerYear,
        description: `${years} ${
          years === 1 ? "year" : "years"
        } × $${formatPrice(workspace.pricePerYear)} AUD`,
      };
    } else if (diffInMonths >= 1 && workspace.pricePerMonth > 0) {
      const months = Math.ceil(diffInMonths);
      return {
        unit: "month",
        quantity: months,
        price: workspace.pricePerMonth,
        total: months * workspace.pricePerMonth,
        description: `${months} ${
          months === 1 ? "month" : "months"
        } × $${formatPrice(workspace.pricePerMonth)} AUD`,
      };
    } else if (diffInWeeks >= 1 && workspace.pricePerWeek > 0) {
      const weeks = Math.ceil(diffInWeeks);
      return {
        unit: "week",
        quantity: weeks,
        price: workspace.pricePerWeek,
        total: weeks * workspace.pricePerWeek,
        description: `${weeks} ${
          weeks === 1 ? "week" : "weeks"
        } × $${formatPrice(workspace.pricePerWeek)} AUD`,
      };
    } else if (diffInDays >= 1 && workspace.pricePerDay > 0) {
      return {
        unit: "day",
        quantity: diffInDays,
        price: workspace.pricePerDay,
        total: diffInDays * workspace.pricePerDay,
        description: `${diffInDays} ${
          diffInDays === 1 ? "day" : "days"
        } × $${formatPrice(workspace.pricePerDay)} AUD`,
      };
    } else if (workspace.pricePerHour > 0) {
      const hours = Math.max(1, Math.ceil(diffInMs / (1000 * 60 * 60))); // Minimum 1 hour
      return {
        unit: "hour",
        quantity: hours,
        price: workspace.pricePerHour,
        total: hours * workspace.pricePerHour,
        description: `${hours} ${
          hours === 1 ? "hour" : "hours"
        } × $${formatPrice(workspace.pricePerHour)} AUD`,
      };
    }

    return {
      unit: "days",
      quantity: 0,
      price: 0,
      total: 0,
      description: "No pricing available",
    };
  };

  const pricingInfo = calculatePricing();

  const handleInputClick = (inputName: "moveIn" | "moveOut") => {
    setActiveInput(inputName);
    setHoveredDate(null); // Reset hovered date when switching pickers
  };

  const handleMoveInDateChange = (date: Date | null) => {
    setMoveInDate(date);
    if (date) {
      // Automatically switch to move-out picker after selecting move-in date
      setActiveInput("moveOut");

      // Calculate the minimum move-out date based on workspace booking requirements
      const minimumMoveOut = calculateMinimumMoveOutDate(date);

      // If the current move-out date is before the minimum required, update it
      if (!moveOutDate || moveOutDate < minimumMoveOut) {
        setMoveOutDate(minimumMoveOut);
      }
    }
  };

  const handleMoveOutDateChange = (date: Date | null) => {
    setMoveOutDate(date);
    if (date) {
      // Close the picker after selecting move-out date
      setActiveInput(null);
      setHoveredDate(null);
    }
  };

  // Function to determine if a date is in the hover range
  const isInHoverRange = (date: Date): boolean => {
    if (!moveInDate || !hoveredDate || activeInput !== "moveOut") return false;

    const dateTime = date.getTime();
    const moveInTime = moveInDate.getTime();
    const hoveredTime = hoveredDate.getTime();

    return dateTime > moveInTime && dateTime <= hoveredTime;
  };

  // Function to add custom class names to dates
  const getDayClassName = (date: Date): string => {
    const classes: string[] = [];

    // Highlight move-in date in move-out picker
    if (
      moveInDate &&
      activeInput === "moveOut" &&
      date.toDateString() === moveInDate.toDateString()
    ) {
      classes.push("highlighted-start-date");
    }

    // Add hover range styling
    if (isInHoverRange(date)) {
      classes.push("hover-range-date");
    }

    // Highlight the hovered date
    if (
      hoveredDate &&
      activeInput === "moveOut" &&
      date.toDateString() === hoveredDate.toDateString()
    ) {
      classes.push("highlighted-hover-date");
    }

    return classes.join(" ");
  };

  // Close datepicker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setActiveInput(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchWorkspace = async () => {
      try {
        //const userId = user?.id;
        const response = await axios.get("/api/Workspace/getWorkspace", {
          params: {
            WorkspaceId: workspaceId,
          },
        });

        if (response.status === 200) {
          console.log("Fetched workspace:", response.data.workspace);
          setWorkspace(response.data.workspace);

          if (response.data.reviews && response.data.reviews.length > 0) {
            const totalRating = response.data.reviews.reduce(
              (sum: number, review: ReviewData) => sum + review.rating,
              0,
            );
            const averageRating = totalRating / response.data.reviews.length;
            setRating(averageRating);
          } else {
            setRating(0); // No reviews, set rating to 0
          }
        } else {
          setError(response.data.message || "Failed to load workspace");
        }
      } catch (err) {
        console.error("Error fetching workspace:", err);
        setError("Failed to fetch workspace details");
      } finally {
        setLoading(false);
      }
    };

    fetchWorkspace();
  }, [workspaceId, user]);

  useEffect(() => {
    if (user && workspace) {
      setIsEditable(user.id === workspace.userId);
    }
  }, [user, workspace]);

  // Set default move-out date based on minimum booking requirements
  useEffect(() => {
    if (workspace && moveInDate && !moveOutDate) {
      const minimumMoveOut = calculateMinimumMoveOutDate(moveInDate);
      setMoveOutDate(minimumMoveOut);
    }
  }, [workspace, moveInDate]);

  // Fetch user's bookings to check if they have booked this workspace
  useEffect(() => {
    const fetchUserBooking = async () => {
      if (!user || !workspaceId) return;

      try {
        const token = Cookies.get("authToken");
        const response = await axios.get(
          `/api/booking/list?UserId=${user.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.data.success && response.data.bookings) {
          // Find an active booking for this workspace (not cancelled or completed)
          const activeBooking = response.data.bookings.find(
            (booking: BookingData) =>
              booking.workspaceId === parseInt(workspaceId) &&
              booking.status !== BookingStatus.Cancelled &&
              booking.status !== BookingStatus.Completed &&
              new Date(booking.endDate) >= new Date(),
          );
          setUserBooking(activeBooking || null);
        }
      } catch (error) {
        console.error("Error fetching user bookings:", error);
      }
    };

    fetchUserBooking();
  }, [user, workspaceId]);

  // Fetch all workspace bookings to exclude booked dates
  useEffect(() => {
    const fetchWorkspaceBookings = async () => {
      if (!workspaceId) return;

      try {
        const response = await axios.get(`/api/booking/list`, {
          params: {
            WorkspaceId: workspaceId,
          },
        });

        if (response.data.success && response.data.bookings) {
          // Filter for active bookings (not cancelled)
          const activeBookings = response.data.bookings.filter(
            (booking: BookingData) =>
              booking.status !== BookingStatus.Cancelled &&
              new Date(booking.endDate) >= new Date(),
          );
          setWorkspaceBookings(activeBookings);
        }
      } catch (error) {
        console.error("Error fetching workspace bookings:", error);
      }
    };

    fetchWorkspaceBookings();
  }, [workspaceId]);

  // Function to check if a date is within any booking's date range
  const isDateBooked = (date: Date): boolean => {
    return workspaceBookings.some((booking) => {
      const startDate = new Date(booking.startDate);
      const endDate = new Date(booking.endDate);

      // Set time to midnight for accurate date comparison
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      // Check if date falls within booking range (inclusive)
      return checkDate >= startDate && checkDate <= endDate;
    });
  };

  if (loading) return <div className="text-center py-5">Loading...</div>;
  if (error) return <div className="text-center py-5 text-danger">{error}</div>;
  if (!workspace)
    return <div className="text-center py-5">Workspace not found</div>;

  return (
    <div className="bg-gray-50 min-h-screen workspacePage-container mx-auto">
      <div className="lg:mx-30 xl:mx-40 ">
        <div className="flex justify-between items-center mb-4">
          <div>
            {isEditable && (
              <button
                onClick={handleBookWorkspace}
                className="secondary-button h-10 flex items-center justify-center"
              >
                Edit Workspace
              </button>
            )}
          </div>
        </div>
        {/* Image Carousel */}
        <div className="flex flex-col mt-10 md:flex-row w-full h-auto md:h-[300px] lg:h-[500px]">
          {/* Main image - full width on mobile, 2/3 on desktop */}
          <div className="w-full md:w-1/2 h-64 md:h-full p-1">
            <img
              src={workspace.images[0]?.imageUrl}
              alt="Main workspace image"
              className="w-full h-full object-cover rounded-t-4xl md:rounded-l-4xl md:rounded-tr-none"
            />
          </div>

          {/* Thumbnail grid - hidden on mobile, 1/3 on desktop */}
          <div className="hidden md:flex md:flex-col md:w-1/2 h-full p-1">
            {/* First row of thumbnails - with gap */}
            <div className="flex flex-row h-1/2 pb-1">
              {workspace.images.slice(1, 3).map((image, index) => (
                <div
                  key={index}
                  className="relative w-1/2 h-full"
                  style={{
                    paddingRight: index === 0 ? "0.25rem" : 0,
                    paddingLeft: index === 1 ? "0.25rem" : 0,
                  }}
                >
                  <img
                    src={image.imageUrl}
                    alt={`Workspace thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    style={{
                      borderTopRightRadius: index === 1 ? "2rem" : 0,
                    }}
                  />
                </div>
              ))}
            </div>

            {/* Second row of thumbnails - with gap */}
            <div className="flex flex-row h-1/2 pt-1">
              {workspace.images.slice(3, 5).map((image, index) => (
                <div
                  key={index + 2}
                  className="relative w-1/2 h-full"
                  style={{
                    paddingRight: index === 0 ? "0.25rem" : 0,
                    paddingLeft: index === 1 ? "0.25rem" : 0,
                  }}
                >
                  <img
                    src={image.imageUrl}
                    alt={`Workspace thumbnail ${index + 3}`}
                    className="w-full h-full object-cover"
                    style={{
                      borderBottomRightRadius: index === 1 ? "2rem" : 0,
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Contact/Tour/Reserve Form - Moved below images */}

        {/* Information */}
        <div className="mt-5 flex flex-row justify-between items-start">
          <div className="w-full">
            {/* Address/Ratings/Common Features and Butttons */}
            <div className="flex flex-row w-full justify-between items-start">
              <div className="flex flex-row justify-between">
                <div className="mb-10">
                  {/* Title */}
                  <div className="mb-5">
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">
                      {workspace.title || "Untitled Workspace"}
                    </h1>
                    <h6 className="text-lg text-gray-500">
                      {workspace.aptUnit && `${workspace.aptUnit}, `}
                      {workspace.floor && `Floor ${workspace.floor}, `}
                      {workspace.address}, {workspace.city}, {workspace.state}{" "}
                      {workspace.postalCode}
                      {workspace.country && `, ${workspace.country}`}
                    </h6>
                  </div>
                  <div className="space-y-4">
                    {/* Row 1: Ratings, Reviews */}
                    <div className="grid grid-cols-[1fr_1fr_3fr] gap-4 items-center">
                      {/* Column 1: Rating */}
                      <div className="flex items-center">
                        <FaStar className="mr-2" />
                        <p>
                          {workspace.reviews?.length > 0
                            ? rating > 0
                              ? rating.toFixed(1)
                              : "No Ratings"
                            : "No Ratings"}
                        </p>
                      </div>

                      {/* Column 2: Reviews */}
                      <div className="flex items-center">
                        <p>
                          {workspace.reviews?.length > 0
                            ? `${workspace.reviews.length} review${
                                workspace.reviews.length > 1 ? "s" : ""
                              }`
                            : "No Reviews"}
                        </p>
                      </div>
                    </div>

                    {/* Row 2: Features */}
                    <div className="grid grid-cols-[1fr_1fr_3fr] gap-4">
                      {/* Capacity */}
                      <div className="flex items-center justify-left py-2 rounded-md">
                        <svg
                          className="w-5 h-5 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                        <span>{workspace.capacity}</span>
                      </div>

                      {/* Size */}
                      <div className="flex items-center justify-left py-2 rounded-md">
                        <SlSizeFullscreen className="mr-2" />
                        <span>{workspace.sizeSqm} m²</span>
                      </div>

                      {/* Workspace Type */}
                      <div className="flex items-center justify-left py-2 rounded-md">
                        {IconComponent && (
                          <IconComponent className="w-5 h-5 mr-2" />
                        )}
                        <span>
                          {workspace.workspaceType.replace(/(^\w|\s\w)/g, (m) =>
                            m.toUpperCase(),
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-3 mt-4">
                    {workspace.isInstantBookable && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Instant Bookable
                      </span>
                    )}
                    {workspace.isFurnished && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                        Furnished
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-15">
              <h2 className="text-2xl mb-4">About This Space</h2>
              <div className="relative">
                <div
                  className={
                    showFullDescription ? "" : "overflow-hidden line-clamp-3"
                  }
                  style={
                    showFullDescription
                      ? {}
                      : {
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                        }
                  }
                >
                  <SafeHTML html={workspace.description} />
                </div>
                {!showFullDescription && workspace.description.length > 50 && (
                  <div className="absolute bottom-0 left-0 w-full h-8 bg-gradient-to-t from-gray-50 to-transparent pointer-events-none"></div>
                )}
              </div>
              {workspace.description.length > 50 && (
                <button
                  className="mt-2 text-lg underline"
                  onClick={() => setShowFullDescription((prev) => !prev)}
                >
                  {showFullDescription ? "View Less" : "View More"}
                </button>
              )}
            </div>
            {/* Amenities */}
            <div className="container mt-4">
              <div className="row">
                <div className="col-md-8">
                  <div className="mb-10">
                    <h2 className="text-2xl mb-10">Amenities</h2>

                    {/* Display first 6 amenities (3 columns x 2 rows) */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-8 mb-4">
                      {workspace.amenities.slice(0, 6).map((amenity) => {
                        const amenityData = amenities.find(
                          (a) => a.value === amenity.id,
                        );
                        const AmenityIcon = amenityData?.icon;

                        return (
                          <div key={amenity.id} className="flex items-center">
                            <span className="mr-2 flex-shrink-0">
                              {AmenityIcon ? (
                                <AmenityIcon className="h-5 w-5" />
                              ) : (
                                <span className="w-6 h-6" />
                              )}
                            </span>
                            {amenity.name}
                          </div>
                        );
                      })}
                    </div>

                    {/* Show "View all amenities" link if there are more than 6 amenities */}
                    {workspace.amenities.length > 6 && !showAllAmenities && (
                      <button
                        className="text-lg underline mt-5 mb-4 hover:text-[var(--secondary)]"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowAllAmenities(true)}
                      >
                        View all amenities
                      </button>
                    )}

                    {/* Expanded section for remaining amenities */}
                    {showAllAmenities && workspace.amenities.length > 6 && (
                      <div className="mt-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-3 mb-4">
                          {workspace.amenities.slice(6).map((amenity) => {
                            const amenityData = amenities.find(
                              (a) => a.value === amenity.id,
                            );
                            const AmenityIcon = amenityData?.icon;

                            return (
                              <div
                                key={amenity.id}
                                className="flex items-center"
                              >
                                <span className="mr-2 flex-shrink-0">
                                  {AmenityIcon ? (
                                    <AmenityIcon className="h-5 w-5" />
                                  ) : (
                                    <span className="w-6 h-6" />
                                  )}
                                </span>
                                {amenity.name}
                              </div>
                            );
                          })}
                        </div>
                        <button
                          className="text-lg underline hover:text-[var(--secondary)]"
                          style={{ cursor: "pointer" }}
                          onClick={() => setShowAllAmenities(false)}
                        >
                          View less
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Property Details */}
              {(() => {
                const hasBuildingDetails =
                  workspace.ceilingHeight ||
                  workspace.floorLoadCapacity ||
                  workspace.zoningType ||
                  workspace.buildingClass ||
                  workspace.yearBuilt ||
                  workspace.renovationYear;
                const hasFinancialDetails =
                  workspace.securityDeposit ||
                  workspace.operatingExpenses ||
                  workspace.taxAssessment ||
                  workspace.commonAreaMaintenance ||
                  workspace.includedUtilities;
                const hasIndustrialDetails =
                  workspace.loadingDockCount ||
                  workspace.dockHeight ||
                  workspace.driveInDoors ||
                  workspace.overheadCranes ||
                  workspace.truckManeuveringSpace ||
                  workspace.sprinklerSystem;
                const hasParkingDetails =
                  workspace.parkingSpotsTotal ||
                  workspace.parkingSpotsReserved ||
                  workspace.parkingFee ||
                  workspace.electricVehicleCharging ||
                  workspace.publicTransportProximity;
                const hasTenantDetails =
                  workspace.tenantImprovementAllowance ||
                  workspace.buildOutStatus ||
                  workspace.landlordWorkLetter;
                const hasSpaceDetails =
                  workspace.rooms ||
                  workspace.meetingRooms ||
                  workspace.desks ||
                  workspace.chairs ||
                  workspace.isFurnished;
                const hasAnyDetails =
                  hasBuildingDetails ||
                  hasFinancialDetails ||
                  hasIndustrialDetails ||
                  hasParkingDetails ||
                  hasTenantDetails ||
                  hasSpaceDetails;

                if (!hasAnyDetails) return null;

                const formatCurrency = (val?: number) =>
                  val
                    ? `$${val.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : null;
                const parsedUtilities: string[] = workspace.includedUtilities
                  ? (() => {
                      try {
                        return JSON.parse(workspace.includedUtilities);
                      } catch {
                        return [];
                      }
                    })()
                  : [];

                return (
                  <div className="mb-10">
                    <h2 className="text-2xl mb-6">Property Details</h2>
                    <div className="space-y-6">
                      {/* Space Details */}
                      {hasSpaceDetails && (
                        <div>
                          <h3 className="text-lg font-medium mb-3 text-gray-700">
                            Space Details
                          </h3>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {workspace.rooms ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Rooms
                                </span>
                                <span className="font-medium">
                                  {workspace.rooms}
                                </span>
                              </div>
                            ) : null}
                            {workspace.meetingRooms ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Meeting Rooms
                                </span>
                                <span className="font-medium">
                                  {workspace.meetingRooms}
                                </span>
                              </div>
                            ) : null}
                            {workspace.desks ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Desks
                                </span>
                                <span className="font-medium">
                                  {workspace.desks}
                                </span>
                              </div>
                            ) : null}
                            {workspace.chairs ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Chairs
                                </span>
                                <span className="font-medium">
                                  {workspace.chairs}
                                </span>
                              </div>
                            ) : null}
                            {workspace.isFurnished && (
                              <div className="bg-green-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Furnished
                                </span>
                                <span className="font-medium text-green-700">
                                  Yes
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Building Details */}
                      {hasBuildingDetails && (
                        <div>
                          <h3 className="text-lg font-medium mb-3 text-gray-700">
                            Building Details
                          </h3>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {workspace.ceilingHeight ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Ceiling Height
                                </span>
                                <span className="font-medium">
                                  {workspace.ceilingHeight} m
                                </span>
                              </div>
                            ) : null}
                            {workspace.floorLoadCapacity ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Floor Load Capacity
                                </span>
                                <span className="font-medium">
                                  {workspace.floorLoadCapacity} kg/m²
                                </span>
                              </div>
                            ) : null}
                            {workspace.zoningType ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Zoning Type
                                </span>
                                <span className="font-medium">
                                  {workspace.zoningType}
                                </span>
                              </div>
                            ) : null}
                            {workspace.buildingClass ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Building Class
                                </span>
                                <span className="font-medium">
                                  {workspace.buildingClass}
                                </span>
                              </div>
                            ) : null}
                            {workspace.yearBuilt ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Year Built
                                </span>
                                <span className="font-medium">
                                  {workspace.yearBuilt}
                                </span>
                              </div>
                            ) : null}
                            {workspace.renovationYear ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Last Renovation
                                </span>
                                <span className="font-medium">
                                  {workspace.renovationYear}
                                </span>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      )}

                      {/* Financial Details */}
                      {hasFinancialDetails && (
                        <div>
                          <h3 className="text-lg font-medium mb-3 text-gray-700">
                            Financial Details
                          </h3>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {workspace.securityDeposit ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Security Deposit
                                </span>
                                <span className="font-medium">
                                  {formatCurrency(workspace.securityDeposit)}
                                </span>
                              </div>
                            ) : null}
                            {workspace.operatingExpenses ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Operating Expenses
                                </span>
                                <span className="font-medium">
                                  {formatCurrency(workspace.operatingExpenses)}
                                  /yr
                                </span>
                              </div>
                            ) : null}
                            {workspace.taxAssessment ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Tax Assessment
                                </span>
                                <span className="font-medium">
                                  {formatCurrency(workspace.taxAssessment)}/yr
                                </span>
                              </div>
                            ) : null}
                            {workspace.commonAreaMaintenance ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  CAM Fees
                                </span>
                                <span className="font-medium">
                                  {formatCurrency(
                                    workspace.commonAreaMaintenance,
                                  )}
                                  /yr
                                </span>
                              </div>
                            ) : null}
                          </div>
                          {parsedUtilities.length > 0 && (
                            <div className="mt-3">
                              <span className="text-xs text-gray-500 block mb-2">
                                Included Utilities
                              </span>
                              <div className="flex flex-wrap gap-2">
                                {parsedUtilities.map((u: string) => (
                                  <span
                                    key={u}
                                    className="bg-[var(--secondary)]/10 text-[var(--secondary)] text-xs font-medium px-2.5 py-1 rounded"
                                  >
                                    {u}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Industrial & Loading */}
                      {hasIndustrialDetails && (
                        <div>
                          <h3 className="text-lg font-medium mb-3 text-gray-700">
                            Industrial & Loading
                          </h3>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {workspace.loadingDockCount ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Loading Docks
                                </span>
                                <span className="font-medium">
                                  {workspace.loadingDockCount}
                                </span>
                              </div>
                            ) : null}
                            {workspace.dockHeight ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Dock Height
                                </span>
                                <span className="font-medium">
                                  {workspace.dockHeight}
                                </span>
                              </div>
                            ) : null}
                            {workspace.truckManeuveringSpace ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Truck Space
                                </span>
                                <span className="font-medium">
                                  {workspace.truckManeuveringSpace}
                                </span>
                              </div>
                            ) : null}
                            {workspace.driveInDoors && (
                              <div className="bg-green-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Drive-in Doors
                                </span>
                                <span className="font-medium text-green-700">
                                  Available
                                </span>
                              </div>
                            )}
                            {workspace.overheadCranes && (
                              <div className="bg-green-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Overhead Cranes
                                </span>
                                <span className="font-medium text-green-700">
                                  Available
                                </span>
                              </div>
                            )}
                            {workspace.sprinklerSystem && (
                              <div className="bg-green-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Sprinkler System
                                </span>
                                <span className="font-medium text-green-700">
                                  Installed
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Parking & Access */}
                      {hasParkingDetails && (
                        <div>
                          <h3 className="text-lg font-medium mb-3 text-gray-700">
                            Parking & Access
                          </h3>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {workspace.parkingSpotsTotal ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Total Parking
                                </span>
                                <span className="font-medium">
                                  {workspace.parkingSpotsTotal} spots
                                </span>
                              </div>
                            ) : null}
                            {workspace.parkingSpotsReserved ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Reserved Spots
                                </span>
                                <span className="font-medium">
                                  {workspace.parkingSpotsReserved}
                                </span>
                              </div>
                            ) : null}
                            {workspace.parkingFee ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Parking Fee
                                </span>
                                <span className="font-medium">
                                  {formatCurrency(workspace.parkingFee)}/mo
                                </span>
                              </div>
                            ) : null}
                            {workspace.electricVehicleCharging ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  EV Charging
                                </span>
                                <span className="font-medium">
                                  {workspace.electricVehicleCharging} stations
                                </span>
                              </div>
                            ) : null}
                            {workspace.publicTransportProximity ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Public Transport
                                </span>
                                <span className="font-medium">
                                  {workspace.publicTransportProximity}
                                </span>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      )}

                      {/* Tenant Improvement */}
                      {hasTenantDetails && (
                        <div>
                          <h3 className="text-lg font-medium mb-3 text-gray-700">
                            Tenant Improvement
                          </h3>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {workspace.tenantImprovementAllowance ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  TI Allowance
                                </span>
                                <span className="font-medium">
                                  {formatCurrency(
                                    workspace.tenantImprovementAllowance,
                                  )}
                                </span>
                              </div>
                            ) : null}
                            {workspace.buildOutStatus ? (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Build-Out Status
                                </span>
                                <span className="font-medium">
                                  {workspace.buildOutStatus}
                                </span>
                              </div>
                            ) : null}
                            {workspace.landlordWorkLetter && (
                              <div className="bg-green-50 rounded-lg p-3">
                                <span className="text-xs text-gray-500 block">
                                  Work Letter
                                </span>
                                <span className="font-medium text-green-700">
                                  Landlord completes improvements
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
          <div className="flex justify-end">
            <div className="bg-gray-50 flex items-start p-4" id="formSection">
              <div className="bg-white rounded-4xl shadow-lg w-full overflow-hidden">
                {/* Tab Buttons */}
                <div className="flex border-b border-gray-200">
                  <button
                    onClick={() => setActiveTab("contact")}
                    className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                      activeTab === "contact"
                        ? "bg-[var(--secondary)]/10 text-[var(--secondary)] border-b-2 border-[var(--secondary)]"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Contact
                  </button>
                  <button
                    onClick={() => setActiveTab("tour")}
                    className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                      activeTab === "tour"
                        ? "bg-[var(--secondary)]/10 text-[var(--secondary)] border-b-2 border-[var(--secondary)]"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Tour
                  </button>
                  <button
                    onClick={() => setActiveTab("reserve")}
                    className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                      activeTab === "reserve"
                        ? "bg-[var(--secondary)]/10 text-[var(--secondary)] border-b-2 border-[var(--secondary)]"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Reserve
                  </button>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6 min-h-[400px] flex flex-col">
                  {/* Contact Form */}
                  {activeTab === "contact" && (
                    <form
                      onSubmit={handleContactFormSubmit}
                      className="flex flex-col space-y-4 flex-grow"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Message *
                        </label>
                        <textarea
                          name="message"
                          value={contactFormData.message}
                          onChange={handleContactFormChange}
                          required
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--secondary)] focus:border-transparent resize-none"
                          placeholder="Enter your message or inquiry"
                        />
                      </div>
                      <div className="flex-grow"></div>
                      {contactMessageSuccess && (
                        <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded-lg text-sm">
                          <p>
                            ✅ Your message has been sent successfully! The
                            workspace owner will receive your message and
                            respond within 24-48 hours.
                          </p>
                          {lastConversationId && (
                            <button
                              type="button"
                              onClick={() =>
                                navigate(
                                  `/messages?conversation=${lastConversationId}`,
                                )
                              }
                              className="mt-2 w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-center"
                            >
                              View Conversation
                            </button>
                          )}
                        </div>
                      )}
                      <button
                        type="submit"
                        disabled={isContactSubmitting}
                        className="secondary-button w-full py-3 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isContactSubmitting ? "Sending..." : "Send Message"}
                      </button>
                    </form>
                  )}

                  {/* Tour Form */}
                  {activeTab === "tour" && (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleBookTourSubmit();
                      }}
                      className="flex flex-col space-y-4 flex-grow"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Message *
                        </label>
                        <textarea
                          name="message"
                          value={contactFormData.message}
                          onChange={handleContactFormChange}
                          required
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--secondary)] focus:border-transparent resize-none"
                          placeholder="Enter your message or inquiry"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Preferred Date *
                        </label>
                        <div
                          className={`flex flex-col border w-full max-w-48 px-4 py-1 rounded-2xl transition-colors cursor-pointer ${
                            activeInput === "moveIn"
                              ? "bg-gray-100"
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() => handleInputClick("moveIn")}
                        >
                          <DatePicker
                            selected={moveInDate}
                            onChange={(date) => {
                              setMoveInDate(date);
                              if (date) {
                                setActiveInput(null); // Close picker after selection
                              }
                            }}
                            selectsStart
                            startDate={moveInDate}
                            minDate={new Date()}
                            filterDate={(date) => !isDateBooked(date)}
                            placeholderText="Add date"
                            className={`w-full focus:outline-none bg-transparent placeholder-gray-500 text-sm cursor-pointer ${
                              moveInDate ? "text-black" : "text-gray-500"
                            }`}
                            wrapperClassName="w-full"
                            dateFormat="d MMM yyyy"
                            open={activeInput === "moveIn"}
                            onFocus={() => handleInputClick("moveIn")}
                            onClickOutside={() => setActiveInput(null)}
                          />
                        </div>
                      </div>
                      <div className="flex-grow"></div>
                      {tourBookingSuccess && (
                        <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded-lg text-sm">
                          <p>
                            ✅ Tour request sent successfully! The workspace
                            owner will respond to you soon.
                          </p>
                          {lastConversationId && (
                            <button
                              type="button"
                              onClick={() =>
                                navigate(
                                  `/messages?conversation=${lastConversationId}`,
                                )
                              }
                              className="mt-2 w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-center"
                            >
                              View Conversation
                            </button>
                          )}
                        </div>
                      )}
                      <button
                        type="submit"
                        disabled={isTourSubmitting}
                        className="secondary-button w-full py-3 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isTourSubmitting ? "Booking Tour..." : "Book Tour"}
                      </button>
                    </form>
                  )}

                  {/* Reserve Form */}
                  {activeTab === "reserve" && (
                    <div
                      ref={datePickerRef}
                      className="flex flex-col flex-grow"
                    >
                      {/* Stay Details */}
                      <div className="mb-6 pb-5 border-b border-gray-100">
                        <div className="mb-3">
                          <label className="block text-xs text-gray-500 mb-2">
                            MOVE IN/OUT DATES
                          </label>
                          {/* Combined Date Range Picker */}
                          <div
                            className={`flex items-center border gap-2 px-4 py-3 rounded-2xl transition-colors cursor-pointer ${
                              activeInput === "moveIn" ||
                              activeInput === "moveOut"
                                ? "bg-gray-100 border-[var(--secondary)]"
                                : "hover:bg-gray-50 border-gray-300"
                            }`}
                            onClick={() => {
                              if (!activeInput) {
                                handleInputClick("moveIn");
                              }
                            }}
                          >
                            {/* Move-in Date Display */}
                            <div className="flex-1">
                              <DatePicker
                                selected={moveInDate}
                                onChange={handleMoveInDateChange}
                                selectsStart
                                startDate={moveInDate}
                                endDate={moveOutDate}
                                minDate={new Date()}
                                filterDate={(date) => !isDateBooked(date)}
                                placeholderText="Select dates"
                                className={`w-full focus:outline-none bg-transparent placeholder-gray-400 text-sm cursor-pointer ${
                                  moveInDate
                                    ? "text-black font-medium"
                                    : "text-gray-500"
                                }`}
                                wrapperClassName="w-full"
                                dateFormat="d MMM yyyy"
                                open={activeInput === "moveIn"}
                                onFocus={() => handleInputClick("moveIn")}
                                onClickOutside={() => setActiveInput(null)}
                              />
                            </div>

                            {/* Arrow Separator */}
                            {moveInDate && moveOutDate && (
                              <div className="text-gray-400 px-1">→</div>
                            )}

                            {/* Move-out Date Display */}
                            <div className="flex-1">
                              <DatePicker
                                selected={moveOutDate}
                                onChange={handleMoveOutDateChange}
                                selectsEnd
                                startDate={moveInDate}
                                endDate={moveOutDate}
                                minDate={calculateMinimumMoveOutDate(
                                  moveInDate,
                                )}
                                filterDate={(date) => !isDateBooked(date)}
                                placeholderText=""
                                className={`w-full focus:outline-none bg-transparent placeholder-gray-400 text-sm cursor-pointer ${
                                  moveOutDate
                                    ? "text-black font-medium"
                                    : "text-gray-500"
                                }`}
                                wrapperClassName="w-full"
                                dateFormat="d MMM yyyy"
                                open={activeInput === "moveOut"}
                                onFocus={() => handleInputClick("moveOut")}
                                onClickOutside={() => {
                                  setActiveInput(null);
                                  setHoveredDate(null);
                                }}
                                dayClassName={getDayClassName}
                                onMonthMouseLeave={() => setHoveredDate(null)}
                                onDayMouseEnter={(date) => {
                                  if (moveInDate && date > moveInDate) {
                                    setHoveredDate(date);
                                  }
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        {/* Minimum Booking Notice */}
                        {workspace && workspace.minimumBookingTime > 0 && (
                          <div className="mt-2 text-xs text-gray-500">
                            Minimum booking: {workspace.minimumBookingTime}{" "}
                            {workspace.minimumBookingType ===
                            BookingType.IsHours
                              ? workspace.minimumBookingTime === 1
                                ? "hour"
                                : "hours"
                              : workspace.minimumBookingType ===
                                  BookingType.IsDays
                                ? workspace.minimumBookingTime === 1
                                  ? "day"
                                  : "days"
                                : workspace.minimumBookingType ===
                                    BookingType.IsWeeks
                                  ? workspace.minimumBookingTime === 1
                                    ? "week"
                                    : "weeks"
                                  : workspace.minimumBookingType ===
                                      BookingType.IsMonths
                                    ? workspace.minimumBookingTime === 1
                                      ? "month"
                                      : "months"
                                    : workspace.minimumBookingType ===
                                        BookingType.IsYears
                                      ? workspace.minimumBookingTime === 1
                                        ? "year"
                                        : "years"
                                      : ""}
                          </div>
                        )}
                      </div>

                      {/* Price Breakdown */}
                      <div className="mb-6">
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <div className="text-gray-800">
                              {pricingInfo.description}
                            </div>
                            <div className="font-medium">
                              ${formatPrice(pricingInfo.total)} AUD
                            </div>
                          </div>

                          <div className="flex justify-between pt-3 mt-3 border-t border-gray-200 text-lg font-bold">
                            <div className="text-gray-800">Total</div>
                            <div className="underline">
                              ${formatPrice(pricingInfo.total)} AUD
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Reserve Button */}
                      <button
                        onClick={handleReserveSubmit}
                        disabled={isSubmitting || !moveInDate || !moveOutDate}
                        className="secondary-button w-full py-4 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? "Creating Booking..." : "Reserve"}
                      </button>

                      {/* Notice */}
                      <div className="text-center mt-4 text-sm">
                        You won't be charged yet, Payment will be processed
                        after reservation is confirmed.
                      </div>

                      <div className="text-center text-red-500 text-sm mt-5 cursor-pointer underline">
                        Report this listing
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Success Modal */}
        {showSuccessModal &&
          bookingData &&
          createPortal(
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
                {/* Close button */}
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="absolute top-4 right-4 z-50 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-all duration-200"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>

                {/* Success content */}
                <div className="p-8 text-center">
                  {/* Success icon */}
                  <div className="mx-auto mb-6 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>

                  {/* Title */}
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">
                    {isInstantBooking
                      ? "Booking Confirmed!"
                      : "Booking Request Submitted!"}
                  </h2>

                  {/* Message */}
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {isInstantBooking
                      ? "Your reservation has been successfully created. You will receive a confirmation email shortly with all the details."
                      : "Your booking request has been submitted successfully. You will receive a confirmation email soon from the landlord. If there are any issues with your request, you will receive emails directly notifying you why the booking may be rejected."}
                  </p>

                  {/* Booking details */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
                    <h3 className="font-semibold text-gray-800 mb-3">
                      Booking Details:
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Booking ID:</span>
                        <span className="font-medium">#{bookingData.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Move-in:</span>
                        <span className="font-medium">
                          {moveInDate?.toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Move-out:</span>
                        <span className="font-medium">
                          {moveOutDate?.toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span className="text-gray-600">Total Amount:</span>
                        <span className="font-bold text-[var(--secondary)]">
                          ${formatPrice(bookingData.totalPrice)} AUD
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full font-medium">
                          {bookingData.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Next steps */}
                  <div className="bg-[var(--secondary)]/10 rounded-xl p-4 mb-6 text-left">
                    <h3 className="font-semibold text-[var(--secondary)] mb-2">
                      Next Steps:
                    </h3>
                    <ul className="text-sm text-[var(--secondary-light)] space-y-1">
                      {isInstantBooking ? (
                        <>
                          <li>• Check your email for confirmation details</li>
                          <li>• Your booking is confirmed immediately</li>
                          <li>• Payment has been processed</li>
                          <li>• You can proceed with your move-in plans</li>
                        </>
                      ) : (
                        <>
                          <li>• Check your email for confirmation details</li>
                          <li>
                            • The landlord will review your booking request
                          </li>
                          <li>
                            • You'll be notified via email once it's approved
                          </li>
                          <li>
                            • If rejected, you'll receive an email with the
                            reason
                          </li>
                          <li>• Payment will be processed upon approval</li>
                        </>
                      )}
                    </ul>
                  </div>

                  {/* Action buttons */}
                  <div className="space-y-3">
                    {lastConversationId && (
                      <button
                        onClick={() => {
                          setShowSuccessModal(false);
                          navigate(
                            `/messages?conversation=${lastConversationId}`,
                          );
                        }}
                        className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-md hover:shadow-lg"
                      >
                        Message the Owner
                      </button>
                    )}
                    <button
                      onClick={() => setShowSuccessModal(false)}
                      className="w-full bg-gradient-to-r from-[var(--secondary)] to-[var(--secondary-25)] hover:from-[var(--secondary-light)] hover:to-[var(--secondary)] text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-md hover:shadow-lg"
                    >
                      Continue Browsing
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )}

        {/* Auth Modal */}
        {isAuthModalOpen && (
          <AuthModal
            isOpen={isAuthModalOpen}
            onClose={() => setIsAuthModalOpen(false)}
          />
        )}

        {/* Stripe Payment Modal */}
        {showStripeModal && workspace && user && (
          <StripePaymentModal
            isOpen={showStripeModal}
            onClose={() => setShowStripeModal(false)}
            workspaceId={parseInt(workspaceId || "0")}
            userId={user.id}
            startDate={moveInDate || new Date()}
            endDate={moveOutDate || new Date()}
            totalPrice={calculatePricing().total}
            workspaceTitle={workspace.title}
            message={`Instant booking from ${moveInDate?.toLocaleDateString()} to ${moveOutDate?.toLocaleDateString()}`}
          />
        )}

        {/* Cancel Booking Confirmation Modal */}
        {/*{showCancelModal &&*/}
        {/*  userBooking &&*/}
        {/*  createPortal(*/}
        {/*    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">*/}
        {/*      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">*/}
        {/*        */}
        {/* Close button */}
        {/*        <button*/}
        {/*          onClick={() => setShowCancelModal(false)}*/}
        {/*          className="absolute top-4 right-4 z-50 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-all duration-200"*/}
        {/*        >*/}
        {/*          <X className="w-4 h-4 text-gray-600" />*/}
        {/*        </button>*/}

        {/*        */}
        {/* Modal content */}
        {/*        <div className="p-8 text-center">*/}
        {/*          */}
        {/* Warning icon */}
        {/*          <div className="mx-auto mb-6 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">*/}
        {/*            <svg*/}
        {/*              className="w-8 h-8 text-red-600"*/}
        {/*              fill="none"*/}
        {/*              stroke="currentColor"*/}
        {/*              viewBox="0 0 24 24"*/}
        {/*            >*/}
        {/*              <path*/}
        {/*                strokeLinecap="round"*/}
        {/*                strokeLinejoin="round"*/}
        {/*                strokeWidth="2"*/}
        {/*                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"*/}
        {/*              />*/}
        {/*            </svg>*/}
        {/*          </div>*/}

        {/*          */}
        {/* Title */}
        {/*          <h2 className="text-2xl font-bold text-gray-800 mb-4">*/}
        {/*            Cancel Booking?*/}
        {/*          </h2>*/}

        {/*          */}
        {/* Message */}
        {/*          <p className="text-gray-600 mb-6 leading-relaxed">*/}
        {/*            Are you sure you want to cancel this booking? This action*/}
        {/*            cannot be undone.*/}
        {/*          </p>*/}

        {/*          */}
        {/* Booking details */}
        {/*          <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">*/}
        {/*            <h3 className="font-semibold text-gray-800 mb-3">*/}
        {/*              Booking Details:*/}
        {/*            </h3>*/}
        {/*            <div className="space-y-2 text-sm">*/}
        {/*              <div className="flex justify-between">*/}
        {/*                <span className="text-gray-600">Booking ID:</span>*/}
        {/*                <span className="font-medium">#{userBooking.id}</span>*/}
        {/*              </div>*/}
        {/*              <div className="flex justify-between">*/}
        {/*                <span className="text-gray-600">Status:</span>*/}
        {/*                <span className="font-medium">{userBooking.status}</span>*/}
        {/*              </div>*/}
        {/*              <div className="flex justify-between">*/}
        {/*                <span className="text-gray-600">Total Amount:</span>*/}
        {/*                <span className="font-bold text-[var(--secondary)]">*/}
        {/*                  ${userBooking.totalPrice.toLocaleString()} AUD*/}
        {/*                </span>*/}
        {/*              </div>*/}
        {/*            </div>*/}
        {/*          </div>*/}

        {/*          */}
        {/* Action buttons */}
        {/*          <div className="flex gap-3">*/}
        {/*            <button*/}
        {/*              onClick={() => setShowCancelModal(false)}*/}
        {/*              disabled={isCancelling}*/}
        {/*              className="flex-1 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-800 font-semibold py-3 px-4 rounded-xl transition-all duration-200"*/}
        {/*            >*/}
        {/*              Keep Booking*/}
        {/*            </button>*/}
        {/*            <button*/}
        {/*              onClick={handleCancelBooking}*/}
        {/*              disabled={isCancelling}*/}
        {/*              className="flex-1 bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 disabled:from-gray-400 disabled:to-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-md hover:shadow-lg disabled:transform-none disabled:shadow-none"*/}
        {/*            >*/}
        {/*              {isCancelling ? "Cancelling..." : "Confirm Cancel"}*/}
        {/*            </button>*/}
        {/*          </div>*/}
        {/*        </div>*/}
        {/*      </div>*/}
        {/*    </div>,*/}
        {/*    document.body*/}
        {/*  )}*/}

        {/* Cancel Booking Success Modal */}
        {/*{showCancelSuccessModal &&*/}
        {/*  createPortal(*/}
        {/*    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">*/}
        {/*      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">*/}
        {/*        */}
        {/* Success content */}
        {/*        <div className="p-8 text-center">*/}
        {/*          */}
        {/* Success icon with animation */}
        {/*          <div className="mx-auto mb-6 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center animate-bounce">*/}
        {/*            <CheckCircle className="w-8 h-8 text-green-600" />*/}
        {/*          </div>*/}

        {/*          */}
        {/* Title */}
        {/*          <h2 className="text-2xl font-bold text-gray-800 mb-4">*/}
        {/*            Booking Cancelled Successfully!*/}
        {/*          </h2>*/}

        {/*          */}
        {/* Message */}
        {/*          <p className="text-gray-600 mb-6 leading-relaxed">*/}
        {/*            Your booking has been cancelled. You will be redirected to*/}
        {/*            the home page shortly.*/}
        {/*          </p>*/}

        {/*          */}
        {/* Loading indicator */}
        {/*          <div className="flex justify-center">*/}
        {/*            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--secondary)]"></div>*/}
        {/*          </div>*/}
        {/*        </div>*/}
        {/*      </div>*/}
        {/*    </div>,*/}
        {/*    document.body*/}
        {/*  )}*/}
      </div>
    </div>
  );
}
